import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { UserPlus, Circle, PhoneOff, MessageSquare } from "lucide-react";
import { SecurityPanel } from "../components/SecurityPanel";
import { useAuth } from "../lib/auth";
import { useMeetingChat } from "../hooks/useMeetingChat";
import { LiveKitStage } from "../components/LiveKitStage";
import { ChatPanel } from "../components/ChatPanel";
import { InviteModal } from "../components/InviteModal";
import { Button } from "../components/ui";
import { api } from "../lib/api";

export function MeetingRoomPage() {
  const { meetingId = "" } = useParams();
  const navigate = useNavigate();
  const { token, loading } = useAuth();
  const [chatOpen, setChatOpen] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [egressActive, setEgressActive] = useState(false);
  const [egressBusy, setEgressBusy] = useState(false);

  const inviteToken = useMemo(() => {
    return (
      sessionStorage.getItem(`invite_${meetingId}`) ||
      sessionStorage.getItem("guest_invite_token") ||
      undefined
    );
  }, [meetingId]);

  const guestName = sessionStorage.getItem("guest_name") || undefined;

  const chat = useMeetingChat({
    meetingId,
    token,
    inviteToken,
    guestName,
    enabled: Boolean(meetingId && (token || (inviteToken && guestName))),
  });

  const participantCount = 1 + chat.peers.length;

  async function toggleRecording() {
    if (!token) return;
    setEgressBusy(true);
    try {
      if (egressActive) {
        await api.stopLiveKitEgress(token, meetingId);
        setEgressActive(false);
      } else {
        await api.startLiveKitEgress(token, meetingId);
        setEgressActive(true);
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Recording failed");
    } finally {
      setEgressBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-400">
        Loading session…
      </div>
    );
  }

  if (!token && !(inviteToken && guestName)) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-ink-300">You need an invite to enter this room.</p>
        <Link to="/login">
          <Button type="button">Sign in with invite</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col gap-3 p-3 md:p-4">
      <header className="glass-panel flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-ink-500">LiveKit meeting</p>
          <h1 className="font-display text-lg font-semibold">Room {meetingId.slice(0, 8)}…</h1>
          <p className="text-xs text-ink-500">
            {chat.connected ? "Chat connected" : "Connecting chat…"}
            {chat.error ? ` · ${chat.error}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="ghost" onClick={() => setChatOpen((v) => !v)}>
            <MessageSquare className="h-4 w-4" />
            Chat
          </Button>
          {token && chat.recordingEnabled && (
            <Button
              type="button"
              variant={egressActive ? "danger" : "ghost"}
              onClick={() => void toggleRecording()}
              disabled={egressBusy}
            >
              <Circle className={`h-4 w-4 ${egressActive ? "fill-red-400 text-red-400" : ""}`} />
              {egressBusy ? "…" : egressActive ? "Stop recording" : "Record"}
            </Button>
          )}
          {token && (
            <Button type="button" variant="ghost" onClick={() => setInviteOpen(true)}>
              <UserPlus className="h-4 w-4" />
              Invite
            </Button>
          )}
          <Button
            type="button"
            variant="danger"
            onClick={() => navigate(token ? "/" : `/join/${inviteToken}`)}
          >
            <PhoneOff className="h-4 w-4" />
            Leave
          </Button>
        </div>
      </header>

      <div className="grid flex-1 gap-3 lg:grid-cols-[1fr_340px] xl:grid-cols-[240px_1fr_340px]">
        <div className="hidden min-h-0 xl:block">
          <SecurityPanel
            status={{
              ...chat.e2eeStatus,
              mediaLayer:
                chat.e2eeStatus.mediaLayer === "insertable-streams"
                  ? "insertable-streams"
                  : "dtls-srtp",
            }}
            remoteFingerprints={chat.remoteFingerprints}
            verified={chat.e2eeVerified}
            onVerify={() => chat.setE2eeVerified(true)}
            liveKit
          />
        </div>
        <div className="flex min-h-[420px] min-h-0 flex-col lg:min-h-[min(72vh,780px)]">
          <LiveKitStage
            meetingId={meetingId}
          authToken={token}
          inviteToken={inviteToken}
          guestName={guestName}
        />
        </div>
        {chatOpen && (
          <div className="min-h-[280px] lg:min-h-0">
            <ChatPanel
              messages={chat.messages}
              onSend={(t) => void chat.sendChat(t)}
              onCreateVote={chat.createVote}
              onCastVote={chat.castVote}
              token={token}
              meetingId={meetingId}
              appendMessage={chat.appendMessage}
              guestUpload={
                !token && inviteToken && guestName
                  ? { inviteToken, guestName }
                  : undefined
              }
            />
          </div>
        )}
      </div>

      {token && (
        <InviteModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          meetingId={meetingId}
          token={token}
        />
      )}
    </div>
  );
}
